/**
 * useDms — @flaxitup DM conversations + messages. Reads
 * marketing.meta_conversation + meta_message. Drafts + sends via
 * meta-dm-reply (proxied through /api/content/dm-reply).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface Conversation {
  id: string;
  participant_username: string | null;
  participant_id: string | null;
  participant_profile_pic: string | null;
  updated_time: string | null;
  unread_count: number;
}
export interface DmMessage {
  id: string;
  conversation_id: string;
  from_username: string | null;
  is_from_me: boolean;
  message_text: string | null;
  created_time: string;
}

export function useConversations(limit = 25) {
  return useQuery<Conversation[]>({
    queryKey: ['dm-conversations', limit],
    queryFn: async () => {
      const { data, error } = await marketing
        .from('meta_conversation')
        .select('id,participant_username,participant_id,participant_profile_pic,updated_time,unread_count')
        .order('updated_time', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Conversation[];
    },
    staleTime: 30_000,
  });
}

export function useMessages(conversationId: string | null) {
  return useQuery<DmMessage[]>({
    queryKey: ['dm-messages', conversationId],
    enabled: !!conversationId,
    queryFn: async () => {
      if (!conversationId) return [];
      const { data, error } = await marketing
        .from('meta_message')
        .select('id,conversation_id,from_username,is_from_me,message_text,created_time')
        .eq('conversation_id', conversationId)
        .order('created_time', { ascending: true })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as DmMessage[];
    },
    staleTime: 15_000,
  });
}

export function useDraftReply() {
  return useMutation({
    mutationFn: async (conversation_id: string) => {
      const r = await fetch('/api/content/dm-reply', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ conversation_id, action: 'draft' }),
      });
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.error ?? `HTTP ${r.status}`);
      return j as { draft: string; reply_id: string; ai_prompt_ms: number };
    },
  });
}

export function useSendReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { conversation_id: string; recipient_id: string; text: string; reply_id?: string }) => {
      const r = await fetch('/api/content/dm-reply', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...input, action: 'send' }),
      });
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.error ?? `HTTP ${r.status}`);
      return j;
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['dm-messages', v.conversation_id] });
      qc.invalidateQueries({ queryKey: ['dm-conversations'] });
    },
  });
}
